// src/pages/FactoryManagerDashboard/FactoryManagerDashboard.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './FactoryManagerDashboard.module.css';
import masaLogo from '../Assets/MasaLogo.png';
import { getNotifications, markNotificationAsRead, deleteNotification } from '../api/notificationApi';
import notifylogo from '../Assets/bell-icon.svg';

const FactoryManagerDashboard = () => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getNotifications();
        setNotifications(response.data);
        const count = response.data.filter(n => !n.isRead).length;
        setUnreadCount(count);
      } catch (error) {
        console.error('Error fetching notifications:', error);
      }
    };
    fetchData();
  }, []);

  const toggleDropdown = () => {
    setShowDropdown((prev) => !prev);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setShowDropdown(false);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => { document.removeEventListener('mousedown', handleClickOutside); };
  }, []);

  const handleMarkAsRead = async (id) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) => 
        prev.map(n => n._id === id ? { ...n, isRead: true } : n)
      );
      const newCount = notifications.filter(n => n._id !== id && !n.isRead).length;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await deleteNotification(id);
      setNotifications((prev) => prev.filter(n => n._id !== id));
      const newCount = notifications.filter(n => n._id !== id && !n.isRead).length;
      setUnreadCount(newCount);
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const handleAddClick = () => navigate('/product-management', { state: { action: 'add' } });
  const handleRemoveClick = () => navigate('/product-management', { state: { action: 'remove' } });
  const handleReturnClick = () => navigate('/product-management', { state: { action: 'add' } });
  const handleRequestClick = () => navigate('/requests-management');
  const handleCreateNewProductClick = () => navigate('/create-new-product');
  const handleEditProductClick = () => navigate('/edit-product');
  const handleCreateNewUserClick = () => navigate('/create-new-user');
  const handleManageUsersClick = () => navigate('/manage-users');
  const handleViewSalesRequests = () => navigate('/sales-requests-management');
  const handleViewHeldOrders = () => navigate('/held-orders'); // New Handler for Held Orders

  return (
    <main className={styles.dashboardView}>
      <header className={styles.header}>
        <img 
          loading="lazy" 
          src={masaLogo}
          className={styles.logo} 
          alt="Dashboard Logo" 
        />
        <h1 className={styles.dashboardTitle}>Factory Manager Dashboard</h1>

        {/* Notification Section */}
        <div className={styles.notificationWrapper} ref={dropdownRef}>
          <div className={styles.bellIcon} onClick={toggleDropdown}>
            <img src={notifylogo} alt="Notification Bell" style={{ width: '24px', height: '24px' }} /> 
            {unreadCount > 0 && <span className={styles.unreadBadge}>{unreadCount}</span>}
          </div>
          {showDropdown && (
            <div className={styles.notificationDropdown}>
              <h2 className={styles.notifHeading}>Notifications</h2>
              {notifications.length === 0 ? (
                <p className={styles.noNotifText}>No notifications yet.</p>
              ) : (
                <div className={styles.notifList}>
                  {notifications.map((notif) => (
                    <div 
                      key={notif._id} 
                      className={`${styles.notifItem} ${notif.isRead ? '' : styles.unreadNotif}`}
                    >
                      <p className={styles.notifMessage}>{notif.message}</p>
                      <p className={styles.notifDate}>{new Date(notif.createdAt).toLocaleString()}</p>
                      <div className={styles.notificationButtons}>
                        {!notif.isRead && (
                          <button 
                            className={styles.markReadButton} 
                            onClick={() => handleMarkAsRead(notif._id)}
                          >
                            Mark as Read
                          </button>
                        )}
                        <button 
                          className={styles.DeleteReadButton} 
                          onClick={() => handleDeleteNotification(notif._id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </header>
      <div className={styles.explanationText}>
        <p>
          Welcome to the <strong>factory management dashboard</strong>. Press <strong>Add</strong> to add new products to the factory's inventory, <strong>Remove</strong> to reduce the product count, <strong>Return</strong> to process product returns, <strong>Requests</strong> to manage inventory requests, or <strong>Create New Product</strong> to add a completely new product to the system.  
          You can also edit or delete products using the pages we set up, or create a new user for system access.
        </p>
      </div>
      <div className={styles.buttonContainer}>
        <button className={styles.addButton} onClick={handleAddClick}>
          <i className="fas fa-plus"></i> Add
        </button>
        <button className={styles.removeButton} onClick={handleRemoveClick}>
          <i className="fas fa-minus"></i> Remove
        </button>
        <button className={styles.returnButton} onClick={handleReturnClick}>
          <i className="fas fa-undo"></i> Return
        </button>
        <button className={styles.requestButton} onClick={handleRequestClick}>
          <i className="fas fa-box"></i> Requests
        </button>
        <button className={styles.createButton} onClick={handleCreateNewProductClick}>
          <i className="fas fa-plus-circle"></i> Create New Product
        </button>
        <button className={styles.editButton} onClick={handleEditProductClick}>
          <i className="fas fa-edit"></i> Manage Products
        </button>
        <button className={styles.createUserButton} onClick={handleCreateNewUserClick}>
          <i className="fas fa-user-plus"></i> Create New User
        </button>
        <button className={styles.manageUsersButton} onClick={handleManageUsersClick}>
          <i className="fas fa-users"></i> Manage Users
        </button>
        <button className={styles.SalesRequestsButton} onClick={handleViewSalesRequests}>
          <i className="fas fa-chart-line"></i> Sales Requests
        </button>
        <button className={styles.heldOrdersButton} onClick={handleViewHeldOrders}>
          <i className="fas fa-hourglass-half"></i> Held Orders
        </button>
      </div>
    </main>
  );
};

export default FactoryManagerDashboard;
